/// <amd-module name="Test/Analytics/AnalyticsHub/AnalyticsHub" />
import { AnalyticsExtensionValidator } from "Analytics/Shared/Common/AnalyticsExtensionValidator";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { AnalyticsHubBreadCrumbService } from "Analytics/Shared/Common/Service/AnalyticsHubBreadCrumbService";
import { IAnalyticsDataService } from "Analytics/Shared/Common/Types";
import { getContextTypeFromUrl, getDefinitionIdFromUrl } from "Analytics/Shared/Utils/URLHelper";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import * as Resources from "Test/Analytics/AnalyticsHub/Resources";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { TestResultsReportView } from "Test/Analytics/TestResultsReportView/ControllerViews/TestResultsReportView";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IAnalyticsPageProps {}

export interface IAnalyticsPageState {
    definitionId: number;
    definitionName: string;
    errorText?: string;
}

export class NewTestAnalyticsPage extends VssComponent<IAnalyticsPageProps, IAnalyticsPageState> {
    private _analyticsDataService: IAnalyticsDataService;
    private _contextType: TCMContracts.TestResultsContextType;

    constructor(props: IAnalyticsPageProps, context: IVssComponentContext) {
        super(props, context);
        this._contextType =
            getContextTypeFromUrl(this.context.pageContext) === "build"
                ? TCMContracts.TestResultsContextType.Build
                : TCMContracts.TestResultsContextType.Release;
        this.state = {} as Readonly<IAnalyticsPageState>;
        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }
    }

    public render() {
        let bodyContent = <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        if (this.state && this.state.errorText) {
            bodyContent = (
                <AnalyticsUnavailableMessage
                    imageName={"ServiceError.svg"}
                    message={Resources.ErrorOccurredText}
                    suggestion={this.state.errorText}
                    ariaDescription={this.state.errorText}
                />
            );
        } else if (this.state && this.state.definitionId > 0) {
            const testReportContext = {
                contextType: this._contextType,
                definitionId: this.state.definitionId
            } as CommonTypes.ITestReportContext;

            bodyContent = (
                <div className="testresults-analytics-report flex-grow flex-column">
                    <TestResultsReportView testReportContext={testReportContext} />
                </div>
            );
        }

        return (
            <Page className="testresults-analytics-hub absolute-fill">
                <Header title={Resources.TestFailureReportHeaderText} titleSize={TitleSize.Large}>
                    {this._renderSubHeader(this.state.definitionName)}
                </Header>

                <AnalyticsExtensionValidator>{bodyContent}</AnalyticsExtensionValidator>
            </Page>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        this._getDefinition();
    }

    private async _getDefinition() {
        // Load the Analytics Service Asyncly
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (this._contextType === TCMContracts.TestResultsContextType.Build) {
            await contributionService.getContributionAsync("ms.vss-analyticsshared-web.analytics-build-service");
            this._analyticsDataService = this.context.pageContext.getService<IAnalyticsDataService>("IBuildAnalyticsDataService");
        } else {
            await contributionService.getContributionAsync("ms.vss-test-analytics-web.release-helper");
            this._analyticsDataService = this.context.pageContext.getService<IAnalyticsDataService>("IReleaseAnalyticsDataService");
        }
        // Get the build defintion and add breadcrumb.
        const definitionId = getDefinitionIdFromUrl(this.context.pageContext);
        this.setState({ definitionId: definitionId });
        if (definitionId > 0) {
            const definitionName = await this._analyticsDataService.fetchDefinitionName(definitionId);
            this.setState({ definitionName: definitionName });

            const breadCrumbService = this.context.pageContext.getService<AnalyticsHubBreadCrumbService>("analytics-hub-breadcrumb-service");
            breadCrumbService.addDefinition(definitionId);
        } else {
            this.setState({ errorText: Resources.NotValidBuildDefinition });
        }
    }

    private _renderSubHeader(definitionName: string): JSX.Element {
        return <div className="testresults-analytics-sub-header fontSizeM fontWeightLighter">{definitionName || ""}</div>;
    }
}

Components.add("newTestAnalyticsHub", NewTestAnalyticsPage);
